import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaClipboard, FaTimes, FaUndo, FaUpload } from 'react-icons/fa';
import Modal from './Modal';
import { Button } from '../shared/ui';
import styles from '../pages/VouchersWorkspacePage.module.css';
import { getVouchers, importVouchers } from '../services/voucherService';
import { VOUCHER_IMPORT_HEADERS as SAMPLE_HEADERS } from '../constants/voucherImportColumns';
import { parseVoucherImportMatrix, parseVoucherImportText } from '../utils/parseVoucherImport';

function parseSpreadsheetBuffer(buffer) {
  try {
    const wb = XLSX.read(buffer, { type: 'array' });
    const name = wb.SheetNames[0];
    if (!name) return { error: 'Workbook has no sheets.' };
    const sheet = wb.Sheets[name];
    const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    return parseVoucherImportMatrix(matrix);
  } catch {
    return { error: 'Could not read this spreadsheet.' };
  }
}

function downloadVoucherSample() {
  const sampleRow = [
    'GANZFRIED , CHAIM',
    '2070211',
    '000000001234567890',
    'P',
    '0',
    '0',
    '0',
    '0',
    '698.70'
  ];
  const csvEscape = (value) => {
    const s = String(value ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = [SAMPLE_HEADERS.join(','), sampleRow.map(csvEscape).join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, 'voucher-import-sample.csv');
}

/** Import picker: show street address and EPPIC ID (not internal location name). */
function locationImportOptionLabel(l) {
  if (!l) return '';
  const line1 = l.address_line1 != null ? String(l.address_line1).trim() : '';
  const city = l.city != null ? String(l.city).trim() : '';
  const state = l.state != null ? String(l.state).trim() : '';
  const zip = l.zip_code != null ? String(l.zip_code).trim() : '';
  const cityPart = [city, state].filter(Boolean).join(', ');
  const tail = [cityPart, zip].filter(Boolean).join(' ');
  const addressStr = [line1, tail].filter(Boolean).join(', ');
  const eppic = l.eppic_id != null ? String(l.eppic_id).trim() : '';
  const parts = [];
  if (addressStr) parts.push(addressStr);
  if (eppic) parts.push(`EPPIC ID ${eppic}`);
  if (parts.length) return parts.join(' · ');
  return l.name ? String(l.name) : `Location #${l.id ?? '?'}`;
}

const IMPORT_LARGE_ROW_HINT = 800;

const IMPORT_COMPARE_FIELDS = [
  'child_name',
  'case_number',
  'eligibility_type',
  'aa',
  'sda',
  'ada',
  'cda',
  'total'
];

const MONEY_FIELDS = new Set(['aa', 'sda', 'ada', 'cda', 'total']);

function normImportStr(s) {
  return String(s ?? '').trim();
}

function normImportMoney(s) {
  if (s == null || s === '') return null;
  const n = Number(String(s).replace(/[$,\s]/g, '').replace(/[()]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function displayImportField(field, v) {
  if (MONEY_FIELDS.has(field)) {
    const n = normImportMoney(v);
    return n == null ? '' : n.toFixed(2);
  }
  return normImportStr(v);
}

function importFieldValuesEqual(field, a, b) {
  if (MONEY_FIELDS.has(field)) {
    const na = normImportMoney(a);
    const nb = normImportMoney(b);
    if (na == null && nb == null) return true;
    if (na == null || nb == null) return false;
    return Math.abs(na - nb) < 0.005;
  }
  return normImportStr(a) === normImportStr(b);
}

function childPeriodKey(caseNumber, childName) {
  return `${normImportStr(caseNumber).toUpperCase()}|${normImportStr(childName).toUpperCase()}`;
}

function existingVoucherLookup(vouchers, locationId) {
  const byAgreement = new Map();
  const byChild = new Map();
  for (const v of vouchers) {
    if (String(v.location_id ?? '') !== String(locationId ?? '')) continue;
    const k = normImportStr(v.agreement_number);
    if (k && !byAgreement.has(k)) byAgreement.set(k, v);
    const ck = childPeriodKey(v.case_number, v.child_name);
    if (ck === '|') continue;
    if (!byChild.has(ck)) byChild.set(ck, []);
    byChild.get(ck).push(v);
  }
  return { byAgreement, byChild };
}

function buildImportPreviewMeta(parsedRows, lookup) {
  return parsedRows.map((row) => {
    const key = normImportStr(row.agreement_number);
    let existing = key ? lookup.byAgreement.get(key) : null;
    if (!existing) {
      const group = lookup.byChild.get(childPeriodKey(row.case_number, row.child_name)) || [];
      if (group.length === 1) existing = group[0];
    }
    if (!existing) {
      return { kind: 'create', changes: {}, ignored: false };
    }
    const changes = {};
    for (const f of IMPORT_COMPARE_FIELDS) {
      const oldV = existing[f];
      const newV = row[f];
      if (!importFieldValuesEqual(f, oldV, newV)) {
        changes[f] = {
          before: displayImportField(f, oldV),
          after: displayImportField(f, newV)
        };
      }
    }
    const kind = Object.keys(changes).length ? 'update' : 'unchanged';
    return { kind, changes, ignored: false };
  });
}

function ImportPreviewDiffCell({ field, row, meta }) {
  const ch = meta?.changes?.[field];
  if (ch) {
    return (
      <span className={styles.importDiff}>
        <span className={styles.importDiffBefore}>{ch.before === '' ? '—' : ch.before}</span>
        <span className={styles.importDiffArrow} aria-hidden>
          →
        </span>
        <span className={styles.importDiffAfter}>{ch.after === '' ? '—' : ch.after}</span>
      </span>
    );
  }
  const display = displayImportField(field, row[field]);
  return display === '' ? '—' : display;
}

function previewRowClass(meta, stylesRef) {
  if (!meta) return '';
  if (meta.ignored) return stylesRef.importPreviewRowIgnored;
  if (meta.kind === 'create') return stylesRef.importPreviewRowCreate;
  if (meta.kind === 'update') return stylesRef.importPreviewRowUpdate;
  return stylesRef.importPreviewRowSame;
}

function previewStatusTextClass(meta, stylesRef) {
  if (!meta) return stylesRef.previewStatusMuted;
  if (meta.ignored) return stylesRef.previewStatusSkipped;
  if (meta.kind === 'create') return stylesRef.previewStatusNew;
  if (meta.kind === 'update') return stylesRef.previewStatusUpdate;
  return stylesRef.previewStatusSame;
}

function previewStatusLabel(meta) {
  if (!meta) return '—';
  if (meta.ignored) return 'Skipped';
  if (meta.kind === 'create') return 'New';
  if (meta.kind === 'update') return 'Update';
  return 'No change';
}

export default function VoucherImportModal({
  open,
  onClose,
  settlementDateId,
  settlementLabel,
  locations,
  onSuccess
}) {
  const [tab, setTab] = useState('paste');
  const [pasteText, setPasteText] = useState('');
  const [parseError, setParseError] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [step, setStep] = useState('input');
  const [locationId, setLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMeta, setPreviewMeta] = useState([]);
  const [ignoreUndoStack, setIgnoreUndoStack] = useState([]);
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setTab('paste');
    setPasteText('');
    setParseError('');
    setParsedRows([]);
    setStep('input');
    setLocationId(locations[0]?.id ? String(locations[0].id) : '');
    setBusy(false);
    setDragOver(false);
    setPreviewLoading(false);
    setPreviewMeta([]);
    setIgnoreUndoStack([]);
  }, [locations]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (locations.length && !locationId) setLocationId(String(locations[0].id));
  }, [locations, locationId]);

  const startPreview = useCallback(async () => {
    if (!settlementDateId || !locationId) return;
    setPreviewLoading(true);
    setParseError('');
    try {
      const raw = await getVouchers(settlementDateId);
      if (!Array.isArray(raw)) {
        throw new Error(typeof raw?.message === 'string' ? raw.message : 'Could not load vouchers for this period.');
      }
      const map = existingVoucherLookup(raw, locationId);
      const meta = buildImportPreviewMeta(parsedRows, map);
      setPreviewMeta(meta);
      setIgnoreUndoStack([]);
      setStep('preview');
    } catch (e) {
      setParseError(e?.message || 'Could not compare with existing vouchers.');
    } finally {
      setPreviewLoading(false);
    }
  }, [settlementDateId, locationId, parsedRows]);

  const toggleIgnoreRow = useCallback((index) => {
    setIgnoreUndoStack((stack) => [...stack, index]);
    setPreviewMeta((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ignored: !m.ignored } : m))
    );
  }, []);

  const undoLastIgnoreChange = useCallback(() => {
    setIgnoreUndoStack((stack) => {
      if (stack.length === 0) return stack;
      const idx = stack[stack.length - 1];
      setPreviewMeta((prev) => {
        const next = [...prev];
        if (next[idx]) next[idx] = { ...next[idx], ignored: !next[idx].ignored };
        return next;
      });
      return stack.slice(0, -1);
    });
  }, []);

  const backFromPreview = useCallback(() => {
    setStep('meta');
    setPreviewMeta([]);
    setIgnoreUndoStack([]);
  }, []);

  const previewSummary = useMemo(() => {
    let createN = 0;
    let updateN = 0;
    let sameN = 0;
    let ignoredN = 0;
    let sum = 0;
    for (let i = 0; i < parsedRows.length; i++) {
      const m = previewMeta[i];
      if (!m) continue;
      if (m.ignored) {
        ignoredN += 1;
        continue;
      }
      if (m.kind === 'create') createN += 1;
      else if (m.kind === 'update') updateN += 1;
      else sameN += 1;
      const t = normImportMoney(parsedRows[i].total);
      if (t != null) sum += t;
    }
    return {
      createN,
      updateN,
      sameN,
      ignoredN,
      sum,
      active: createN + updateN + sameN
    };
  }, [parsedRows, previewMeta]);

  const ingestRows = (rows) => {
    setParsedRows(rows);
    setParseError('');
    setStep('meta');
  };

  const handleFile = (file) => {
    setParseError('');
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'txt', 'xlsx', 'xls'].includes(ext)) {
      setParseError('Use .csv, .txt, or .xlsx');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setParseError('File must be under 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (ext === 'xlsx' || ext === 'xls') {
        const res = parseSpreadsheetBuffer(reader.result);
        if (res.error) setParseError(res.error);
        else ingestRows(res.rows);
      } else {
        const text = reader.result;
        const res = parseVoucherImportText(typeof text === 'string' ? text : '');
        if (res.error) setParseError(res.error);
        else ingestRows(res.rows);
      }
    };
    if (ext === 'xlsx' || ext === 'xls') reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  const droppedFile = (e) => {
    const types = e.dataTransfer?.types ? Array.from(e.dataTransfer.types) : [];
    if (!types.includes('Files')) return null;
    e.preventDefault();
    e.stopPropagation();
    return e.dataTransfer.files?.[0] || null;
  };

  const handlePastePreview = () => {
    setParseError('');
    const res = parseVoucherImportText(pasteText);
    if (res.error) {
      setParseError(res.error);
      return;
    }
    ingestRows(res.rows);
  };

  const triggerBrowse = (e) => {
    e.stopPropagation();
    e.preventDefault();
    fileRef.current?.click();
  };

  const runImport = async () => {
    if (!settlementDateId || !locationId) {
      setParseError('Pick a location.');
      return;
    }
    const vouchers = parsedRows.filter((_, i) => !previewMeta[i]?.ignored);
    if (vouchers.length === 0) {
      setParseError('All rows are skipped. Un-skip at least one row or go back.');
      return;
    }
    setBusy(true);
    setParseError('');
    try {
      await importVouchers({
        vouchers,
        location_id: locationId,
        settlement_date_id: settlementDateId
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      setParseError(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const lockedBanner = settlementLabel ? (
    <div className={styles.importLockedBanner}>
      <strong>Settlement period</strong>
      <span>{settlementLabel}</span>
      <span className={styles.importLockedHint}>Imports attach to this period (sidebar).</span>
    </div>
  ) : (
    <div className={styles.importLockedBanner}>Select a settlement period in the sidebar first.</div>
  );

  const modalTitle =
    step === 'input'
      ? 'Import voucher rows'
      : step === 'meta'
        ? 'Location for import'
        : 'Review import';

  const busyOverlay = busy ? (
    <div className={styles.importBusyOverlay} role="status" aria-live="polite" aria-busy="true">
      <div className={styles.importBusyRing} />
      <p className={styles.importBusyLabel}>Importing voucher rows…</p>
      <div className={styles.importBusyTrack} aria-hidden>
        <div className={styles.importBusyFill} />
      </div>
    </div>
  ) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      description={
        step === 'input'
          ? 'Upload or paste an NJ Provider Payments export, or the 9-column voucher template.'
          : undefined
      }
      size={step === 'preview' ? 'xl' : 'lg'}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
      closeDisabled={busy}
      warmTone
      bodyFill={step === 'preview'}
      bodyClassName={styles.importModalBody}
    >
      <div
        className={[styles.importModalRoot, step === 'preview' ? styles.importModalRootReview : '']
          .filter(Boolean)
          .join(' ')}
      >
        {!settlementDateId ? (
          <div className={styles.formStack}>
            {lockedBanner}
            <div className={styles.importFooterBar}>
              <span />
              <div className={styles.importFooterActions}>
                <Button variant="secondary" type="button" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        ) : step === 'input' ? (
          <div className={styles.formStack}>
            {lockedBanner}

            <div className={styles.importTabs}>
              <button
                type="button"
                className={[styles.importTab, tab === 'paste' ? styles.importTabActive : ''].join(' ')}
                onClick={() => {
                  setDragOver(false);
                  setTab('paste');
                }}
              >
                <FaClipboard aria-hidden /> Paste CSV
              </button>
              <button
                type="button"
                className={[styles.importTab, tab === 'upload' ? styles.importTabActive : ''].join(' ')}
                onClick={() => {
                  setDragOver(false);
                  setTab('upload');
                }}
              >
                <FaUpload aria-hidden /> Upload file
              </button>
            </div>

            {tab === 'upload' ? (
              <div
                className={[styles.importDropzone, dragOver ? styles.importDropzoneActive : ''].join(' ')}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleFile(f);
                }}
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click();
                }}
              >
                <div>
                  <div className={styles.importDropIcon}>
                    <FaUpload aria-hidden />
                  </div>
                  <p className={styles.importDropTitle}>
                    Drop your files here, or{' '}
                    <span
                      className={styles.importBrowseAccent}
                      onClick={triggerBrowse}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') triggerBrowse(e);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      click to browse
                    </span>
                  </p>
                  <p className={styles.importDropSub}>.csv and .xlsx · NJ Provider Payments export or 9-column template</p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,.txt,.xlsx,.xls"
                    className={styles.hiddenFileInput}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFile(f);
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>
            ) : (
              <div
                className={[styles.importPasteDrop, dragOver ? styles.importPasteDropActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onDragEnter={(e) => {
                  if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragOver={(e) => {
                  if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setDragOver(false);
                }}
                onDrop={(e) => {
                  const file = droppedFile(e);
                  setDragOver(false);
                  if (file) handleFile(file);
                }}
              >
                <textarea
                  className={styles.importTextarea}
                  rows={12}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste the NJ Provider Payments export (include the header row), or drop an Excel file here. Child CIN matches the student."
                />
                {dragOver ? (
                  <div className={styles.importPasteDropHint} aria-hidden>
                    Drop Excel file
                  </div>
                ) : null}
              </div>
            )}

            {parseError ? <div className={styles.importError}>{parseError}</div> : null}

            <div className={styles.importFooterBar}>
              <button type="button" className={styles.importSampleBtn} onClick={downloadVoucherSample}>
                Download sample file
              </button>
              <div className={styles.importFooterActions}>
                <Button variant="secondary" type="button" onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
                {tab === 'paste' ? (
                  <Button type="button" onClick={handlePastePreview} disabled={!pasteText.trim() || busy}>
                    Continue
                  </Button>
                ) : (
                  <span className={styles.importFooterHint}>Drop a file or browse above</span>
                )}
              </div>
            </div>
          </div>
        ) : step === 'meta' ? (
          <div className={styles.formStack}>
            {lockedBanner}
            <p className={styles.muted}>
              Parsed <strong>{parsedRows.length}</strong> rows. Pick the voucher location.
            </p>
            <label className={styles.sidebarLabel}>Location</label>
            <select
              className={styles.sidebarSelect}
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">Select location</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {locationImportOptionLabel(l)}
                </option>
              ))}
            </select>
            {parseError ? <div className={styles.importError}>{parseError}</div> : null}
            <div className={styles.importFooterBar}>
              <button type="button" className={styles.importSampleBtn} onClick={downloadVoucherSample}>
                Download sample file
              </button>
              <div className={styles.importFooterActions}>
                <Button variant="secondary" type="button" onClick={() => setStep('input')}>
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={startPreview}
                  disabled={!locationId || previewLoading || busy}
                  loading={previewLoading}
                >
                  Preview
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className={`${styles.formStack} ${styles.importReviewStack}`}>
            {lockedBanner}
            <p className={styles.importReviewIntro}>
              Importing to{' '}
              <strong className={styles.importReviewLocation}>
                {locationImportOptionLabel(locations.find((x) => String(x.id) === String(locationId))) || '—'}
              </strong>
              . Click the <strong>×</strong> to skip a row; use <strong>Undo</strong> to restore the last skip.
            </p>
            <div className={styles.importPreviewToolbar}>
              <div className={styles.importPreviewLegend} role="list">
                <div className={styles.importLegendChip} role="listitem">
                  <span className={`${styles.importLegendDot} ${styles.importLegendSwatchCreate}`} aria-hidden />
                  New
                </div>
                <div className={styles.importLegendChip} role="listitem">
                  <span className={`${styles.importLegendDot} ${styles.importLegendSwatchUpdate}`} aria-hidden />
                  Update
                </div>
                <div className={styles.importLegendChip} role="listitem">
                  <span className={`${styles.importLegendDot} ${styles.importLegendSwatchSame}`} aria-hidden />
                  No change
                </div>
                <div className={styles.importLegendChip} role="listitem">
                  <span className={`${styles.importLegendDot} ${styles.importLegendSwatchIgnored}`} aria-hidden />
                  Skipped
                </div>
              </div>
              {ignoreUndoStack.length > 0 ? (
                <button
                  type="button"
                  className={styles.importUndoBtn}
                  onClick={undoLastIgnoreChange}
                  disabled={busy}
                >
                  <span className={styles.importUndoBtnIcon} aria-hidden>
                    <FaUndo />
                  </span>
                  <span className={styles.importUndoBtnLabel}>Undo last skip</span>
                </button>
              ) : null}
            </div>
            <div className={styles.importPreviewStats} aria-live="polite">
              <span className={styles.importPreviewStat}>
                <span className={styles.importPreviewStatValue}>{previewSummary.createN}</span>
                new
              </span>
              <span className={styles.importPreviewStatSep} aria-hidden />
              <span className={styles.importPreviewStat}>
                <span className={styles.importPreviewStatValue}>{previewSummary.updateN}</span>
                update
              </span>
              <span className={styles.importPreviewStatSep} aria-hidden />
              <span className={styles.importPreviewStat}>
                <span className={styles.importPreviewStatValue}>{previewSummary.sameN}</span>
                unchanged
              </span>
              <span className={styles.importPreviewStatSep} aria-hidden />
              <span className={styles.importPreviewStat}>
                <span className={styles.importPreviewStatValue}>{previewSummary.ignoredN}</span>
                skipped
              </span>
            </div>
            <div className={`${styles.previewTableWrap} ${styles.previewTableWrapReview}`}>
              <table className={`${styles.previewTable} ${styles.previewTableReview}`}>
                <thead>
                  <tr>
                    <th className={styles.previewTableColSkip} scope="col">
                      <span className={styles.previewTableHeadMuted}>×</span>
                    </th>
                    <th scope="col">Status</th>
                    <th scope="col">Name</th>
                    <th scope="col">Case #</th>
                    <th scope="col">Agreement #</th>
                    <th className={styles.previewTableHeadNum} scope="col">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.length === 0 ? (
                    <tr>
                      <td className={styles.previewTableEmpty} colSpan={6}>
                        No rows to show.
                      </td>
                    </tr>
                  ) : (
                    parsedRows.map((v, i) => {
                      const meta = previewMeta[i];
                      const rowCls = previewRowClass(meta, styles);
                      const statusTextCls = previewStatusTextClass(meta, styles);
                      return (
                        <tr key={`${i}-${v.agreement_number}`} className={rowCls}>
                          <td className={styles.previewTableColSkip}>
                            <button
                              type="button"
                              className={[
                                styles.importSkipBtn,
                                meta?.ignored ? styles.importSkipBtnActive : ''
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onClick={() => toggleIgnoreRow(i)}
                              disabled={busy}
                              aria-pressed={!!meta?.ignored}
                              title={meta?.ignored ? 'Include in import' : 'Skip this row'}
                              aria-label={
                                meta?.ignored
                                  ? `Include row ${i + 1} in import`
                                  : `Skip row ${i + 1} for import`
                              }
                            >
                              <FaTimes aria-hidden />
                            </button>
                          </td>
                          <td className={styles.previewTableColStatus}>
                            <span className={statusTextCls}>{previewStatusLabel(meta)}</span>
                          </td>
                          <td className={styles.previewTableCellData}>
                            <ImportPreviewDiffCell field="child_name" row={v} meta={meta} />
                          </td>
                          <td className={styles.previewTableCellData}>
                            <ImportPreviewDiffCell field="case_number" row={v} meta={meta} />
                          </td>
                          <td className={`${styles.previewTableMono} ${styles.previewTableCellData}`}>
                            {normImportStr(v.agreement_number) || '—'}
                          </td>
                          <td className={`${styles.previewTableNum} ${styles.previewTableCellData}`}>
                            <ImportPreviewDiffCell field="total" row={v} meta={meta} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td className={styles.previewTableFootCell} colSpan={6}>
                      <span className={styles.previewTableFootMain}>
                        <strong>{previewSummary.active}</strong> rows included
                        <span className={styles.previewTableFootSep} aria-hidden>
                          ·
                        </span>
                        Total <strong>${previewSummary.sum.toFixed(2)}</strong>
                      </span>
                      {previewSummary.ignoredN > 0 ? (
                        <span className={styles.previewTableFootMuted}>
                          {previewSummary.ignoredN} skipped (excluded from sum)
                        </span>
                      ) : null}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {parsedRows.length > IMPORT_LARGE_ROW_HINT ? (
              <p className={styles.muted}>
                Large import ({parsedRows.length.toLocaleString()} rows) — scroll the table to review and skip rows as
                needed.
              </p>
            ) : null}
            {parseError ? <div className={styles.importError}>{parseError}</div> : null}
            <div className={styles.importFooterBar}>
              <button type="button" className={styles.importSampleBtn} onClick={downloadVoucherSample}>
                Download sample file
              </button>
              <div className={styles.importFooterActions}>
                <Button variant="secondary" type="button" onClick={backFromPreview} disabled={busy}>
                  Back
                </Button>
                <Button type="button" loading={busy} onClick={runImport} disabled={previewSummary.active === 0}>
                  Import {previewSummary.active} rows
                </Button>
              </div>
            </div>
          </div>
        )}
        {busyOverlay}
      </div>
    </Modal>
  );
}
